import React from 'react';
import { useAct, gsap, DEBUG, prefersReduced } from '../hooks/useScrollStory';

/* ══════════════════════════════════════════════════════
   TELEFON DESTESİ
   Telefonlar hazır mockup, arka planları saydam.
   Kendi çizdiğim CSS çerçeve kaldırıldı.

   Kaydırdıkça öndeki telefon gerçekten düşüyor:
   1) denge kaybı  — alt kenarını eksen alıp hafifçe yatar
   2) yerçekimi    — hızlanarak iner (power2.in, karesel ivme)
   3) savrulma     — düşerken döner, yana kayar, hafif bulanır
   Silinme havada değil, kadrajı terk ederken oluyor.
   ══════════════════════════════════════════════════════ */

const SCREENS = [
  {
    src: '/assets/app/p1-home.webp',
    tag: 'Ana sayfa',
    title: 'Tarlan açılışta karşında',
    body: 'Hava durumu, akıllı uyarılar ve hızlı işlemler ilk ekranda. Ne yapman gerektiğini aramıyorsun, sana geliyor.',
    points: ['Canlı hava ve UV takibi', 'Sulama ve zararlı uyarıları', 'Tek dokunuşla analiz'],
    accent: '#4FB98A',
  },
  {
    src: '/assets/app/p2-satellite.webp',
    tag: 'Uydu analizi',
    title: 'Tarlanı uydudan oku',
    body: 'Sentinel-2 geçişleriyle NDVI haritası çıkıyor. Kırmızı stresli, yeşil sağlıklı. Hangi köşenin geride kaldığını gözünle görüyorsun.',
    points: ['Bitki, su ve klorofil katmanları', 'Tarla sağlığı puanı', 'Verim tahmini ve son çekim tarihi'],
    accent: '#C8FF22',
  },
  {
    src: '/assets/app/p3-disease.webp',
    tag: 'Hastalık analizi',
    title: 'Yaprağı çek, teşhisi al',
    body: 'Fotoğrafı yapay zeka tarıyor. Hastalık türünü, güven oranını ve risk seviyesini veriyor, ardından ne uygulaman gerektiğini yazıyor.',
    points: ['Latince adıyla tür teşhisi', 'Güven oranı ve risk seviyesi', 'Uygulama önerisi'],
    accent: '#E0A83C',
  },
  {
    src: '/assets/app/p4-fielddata.webp',
    tag: 'Tarla verileri',
    title: 'Her parselin kendi karnesi',
    body: 'Verim tahmini, sağlık puanı, finansal durum ve piyasa fiyatı tek ekranda. Tarlanın nasıl gittiğini rakamla görüyorsun.',
    points: ['Tarla verimi ve sağlık puanı', 'Finansal takip', 'Güncel piyasa fiyatı'],
    accent: '#3E8E6B',
  },
  {
    src: '/assets/app/p5-chat.webp',
    tag: 'Agren AI sohbet',
    title: 'Sor, Türkçe cevap al',
    body: 'Tarlana özel soru soruyorsun. Cevap Tarım Bakanlığı kaynaklarına dayanıyor ve senin parseline göre veriliyor.',
    points: ['Parsel adıyla soru sorma', 'Kaynaklı öneri', 'Hazır soru kısayolları'],
    accent: '#5AD1A0',
  },
  {
    src: '/assets/app/p6-voice.webp',
    tag: 'Tarım asistanı',
    title: 'Takıldığın yerde Agren\'e sor',
    body: 'Tarım hakkında merak ettiğini Agren AI\'ye sor. Bitki bakımı, tarla yönetimi ve günlük üretim kararlarında sana bağlamı anlayan bir yardımcı sunar.',
    points: ['Tarımla ilgili sorularını sor', 'Yanıtı tarla bağlamında al', 'Yeni sorularla devam et'],
    accent: '#8BE0C4',
  },
];

const UNIT_VH = 100;
const UNITS = SCREENS.length + 0.9;
const ACTIVE_AT = 0.64;

export default function PhoneStack() {
  const ref = useAct((root) => {
    const q = gsap.utils.selector(root);
    const phones = q('.ph');
    const panels = q('.info');

    const stack = {
      yPercent: 6, xPercent: 0, scale: 0.93, opacity: 0.4,
      rotate: 0, filter: 'blur(3px)', transformOrigin: '50% 50%',
    };
    const active = {
      yPercent: 0, xPercent: 0, scale: 1, opacity: 1,
      rotate: 0, filter: 'blur(0px)', transformOrigin: '50% 50%',
    };

    if (prefersReduced()) {
      gsap.set(phones, active);
      gsap.set(panels, { opacity: 1, y: 0 });
      return;
    }

    gsap.set(phones[0], active);
    gsap.set(phones.slice(1), stack);
    gsap.set(panels, { opacity: 0, y: 26 });
    gsap.set(panels[0], { opacity: 1, y: 0 });

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: root,
        start: 'top top',
        end: 'bottom bottom',
        scrub: 1.15,
        markers: DEBUG,
      },
    });

    phones.forEach((el, i) => {
      if (i >= phones.length - 1) return;
      /* 1) denge kaybı */
      tl.to(el, { transformOrigin: '50% 100%', rotate: -5, yPercent: -3, ease: 'power1.out', duration: 0.2 }, i + 0.4);
      /* 2) yerçekimi: karesel ivme */
      tl.to(el, { yPercent: 158, xPercent: 12, rotate: 30, ease: 'power2.in', duration: 0.68 }, i + 0.6);
      /* 3) hızlanınca hareket bulanıklığı */
      tl.to(el, { filter: 'blur(3px)', ease: 'power2.in', duration: 0.5 }, i + 0.72);
      /* 4) kadrajı terk ederken silinir */
      tl.to(el, { opacity: 0, ease: 'none', duration: 0.2 }, i + 1.06);
      /* arkadaki öne çıkar, hafif yaylanmayla */
      tl.to(phones[i + 1], { ...active, ease: 'back.out(1.15)', duration: 0.6 }, i + ACTIVE_AT);
    });

    /* metin panelleri: biri tamamen çıkmadan diğeri girmiyor */
    panels.forEach((el, i) => {
      if (i > 0) tl.fromTo(el, { opacity: 0, y: 26 }, { opacity: 1, y: 0, ease: 'power3.out', duration: 0.34 }, i - 0.32);
      if (i < panels.length - 1) tl.to(el, { opacity: 0, y: -20, ease: 'power2.in', duration: 0.28 }, i + 0.32);
    });

    q('.tick-fill').forEach((el, i) => {
      gsap.set(el, { scaleX: i === 0 ? 1 : 0, transformOrigin: 'left center' });
      if (i > 0) tl.to(el, { scaleX: 1, ease: 'none', duration: 0.45 }, i - 0.35);
    });

    const counter = q('.ph-count')[0];
    if (counter) {
      const st = { v: 0 };
      tl.to(
        st,
        {
          v: UNITS,
          ease: 'none',
          duration: UNITS,
          onUpdate: () => {
            const idx = Math.max(0, Math.min(SCREENS.length - 1, Math.round(st.v - (1 - ACTIVE_AT))));
            const txt = `${String(idx + 1).padStart(2, '0')} / ${String(SCREENS.length).padStart(2, '0')}`;
            if (counter.textContent !== txt) counter.textContent = txt;
          },
        },
        0
      );
    }

    tl.to(q('.ph-stage'), { opacity: 0, ease: 'power2.inOut', duration: 0.5 }, UNITS - 0.55);
  }, []);

  return (
    <section
      ref={ref}
      id="uygulama"
      className="relative"
      style={{ height: `${UNITS * UNIT_VH}vh`, background: '#050605' }}
    >
      <div className="ph-stage sticky top-0 h-screen overflow-hidden">
        {/* Zemin: hero videosunun bittiği toprak. Hero'dan buraya
            geçerken zemin değişmiyor, aynı yerin devamı gibi duruyor. */}
        <div
          className="absolute inset-0"
          aria-hidden="true"
          style={{
            backgroundImage: 'url(/assets/soil/hero-soil.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          aria-hidden="true"
          style={{
            background:
              'linear-gradient(180deg, rgba(5,6,5,.55) 0%, rgba(5,6,5,.72) 100%), radial-gradient(58% 55% at 72% 46%, rgba(46,125,83,.26), transparent 70%)',
          }}
        />

        <div className="relative h-full shell grid lg:grid-cols-[1fr_auto] items-center gap-[clamp(24px,3vw,52px)]">
          {/* sol: her ekranın kendi anlatımı */}
          <div className="relative order-2 lg:order-1 max-w-[min(440px,92vw)]">
            <div className="flex items-center gap-3 mb-6">
              <span className="ph-count font-mono text-[12px] tracking-[.14em] text-muted">01 / 06</span>
              <div className="flex gap-1.5">
                {SCREENS.map((s) => (
                  <i
                    key={s.tag}
                    className="block w-7 h-[2px] rounded-full overflow-hidden"
                    style={{ background: 'rgba(247,247,242,.18)' }}
                  >
                    <span className="tick-fill block w-full h-full" style={{ background: '#C8FF22' }} />
                  </i>
                ))}
              </div>
            </div>

            <div className="relative min-h-[clamp(270px,36vh,350px)]">
              {SCREENS.map((s) => (
                <div key={s.tag} className="info absolute inset-x-0 top-0">
                  <span className="font-mono text-[11px] tracking-[.14em] uppercase" style={{ color: s.accent }}>
                    {s.tag}
                  </span>
                  <h2
                    className="mt-4 font-display font-bold text-white leading-[1.06] tracking-[-0.028em] clip-safe"
                    style={{ fontSize: 'clamp(26px,3.4vw,48px)' }}
                  >
                    {s.title}
                  </h2>
                  <p className="mt-5 text-[15px] leading-relaxed text-white/70 max-w-[42ch]">{s.body}</p>
                  <ul className="mt-6 grid gap-2.5">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-start gap-3 text-[14px] text-white/85">
                        <span className="mt-[7px] w-1.5 h-1.5 rounded-full shrink-0" style={{ background: s.accent }} />
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* sağ: hazır mockuplar, saydam arka plan, çerçeve görselin içinde */}
          <div
            className="relative order-1 lg:order-2 justify-self-center"
            style={{ width: 'clamp(340px,42vw,620px)', aspectRatio: '736 / 981' }}
          >
            {SCREENS.map((s, i) => (
              <img
                key={s.src}
                src={s.src}
                alt={`Agren AI uygulaması, ${s.tag} ekranı`}
                loading={i < 2 ? 'eager' : 'lazy'}
                draggable="false"
                className="ph absolute inset-0 w-full h-full object-contain will-change-transform"
                style={{ zIndex: SCREENS.length - i, filter: 'drop-shadow(0 40px 60px rgba(0,0,0,.55))' }}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
